import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useMerchantPayoutsPausingStatus, useShouldShowPayoutsPausingAlert } from '@frontend/payments';
import { useMerchants } from '@frontend/payments-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const usePaymentsNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds } = useAppScopeStore();

  const firstLocationId = selectedLocationIds[0];
  const { hasAccountAlert: hasPaymentAccountAlert } = useMerchants(firstLocationId)['firstLocation'];
  const { anyLocationHasPayments } = useMerchants(selectedLocationIds);
  const { status: paymentAccountAlertType } = useMerchantPayoutsPausingStatus(firstLocationId);
  const shouldShowPaymentsAccountAlert = useShouldShowPayoutsPausingAlert(firstLocationId);

  const subNavItems = useMemo(() => {
    const getHasPaymentsNavItems = () => {
      return [
        {
          label: t('Requests'),
          relativePath: '/invoices',
          trackingId: 'nav-payments-invoices',
          description:
            'Lists all issued invoices and payment requests, with options to view details, send to customers, or track payment status.',
        },
        {
          label: t('Payouts'),
          relativePath: '/payouts',
          trackingId: 'nav-payments-payouts',
          description:
            'Shows disbursements/payouts received or pending to your practice account, including settlement dates and amounts.',
        },
        {
          label: t('Payment Plans'),
          relativePath: '/payment-plans',
          trackingId: 'nav-payments-payment-plans',
          description:
            'Create and manage recurring billing plans for patients or clients, including installment schedules and balances.',
        },
      ];
    };

    const getNoPaymentsNavItems = () => {
      return [
        {
          label: t('Get Payments'),
          /**
           * Give it the same relative path as "Requests" (above), so that
           * when switching accounts, users don't end up on a stranded URL.
           */
          relativePath: '/invoices',
          trackingId: 'nav-payments-signup',
          description:
            'Start accepting payments by setting up your Weave Payments account. Quick and easy onboarding to begin processing transactions.',
        },
      ];
    };

    return createSubNavItems(anyLocationHasPayments ? getHasPaymentsNavItems() : getNoPaymentsNavItems());
  }, [t, anyLocationHasPayments]);

  return useMemo(
    () =>
      createNavItem('payments', {
        hasCount: !!hasPaymentAccountAlert && shouldShowPaymentsAccountAlert,
        hasCountColor: paymentAccountAlertType === 'error' ? theme.colors.critical50 : theme.colors.warning50,
        iconName: 'payments',
        label: t('Payments'),
        root: '/payments',
        subNavItems,
        settings: { path: '/payments', label: '' },
      }),
    [t, hasPaymentAccountAlert, shouldShowPaymentsAccountAlert, paymentAccountAlertType, subNavItems]
  );
};
